import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { SplitText } from 'gsap/SplitText'
import type React from 'react'

gsap.registerPlugin(SplitText)

export interface ScrambleBlurInOptions {
    // Seconds before the first character starts
    delay?: number
    // Seconds each character takes to settle
    duration?: number
    // Seconds between one character starting and the next
    stagger?: number
    // How blurred (px) and how far below (fraction of the line) each character starts
    blur?: number
    rise?: number
    // Fraction of each character's duration spent cycling random glyphs
    scramble?: number
    // Called once every character has landed
    onComplete?: () => void
}

const DEFAULTS: Required<Omit<ScrambleBlurInOptions, 'onComplete'>> = {
    delay: 0,
    duration: 0.7,
    stagger: 0.035,
    blur: 8,
    rise: 0.3,
    scramble: 0.55,
}

const UPPER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
const LOWER = 'abcdefghijklmnopqrstuvwxyz'
const DIGITS = '0123456789'
// Minimum time a random glyph stays up, so the scramble reads as flicker, not noise
const SWAP_MS = 70

// Random glyphs from the same family as the real character, so uppercase text
// scrambles in capitals and numbers scramble as numbers. Punctuation and
// symbols ("&", ".") aren't scrambled, just rolled in.
const pool = (char: string) =>
    /[A-Z]/.test(char) ? UPPER : /[a-z]/.test(char) ? LOWER : /[0-9]/.test(char) ? DIGITS : null

// Each visible character's advance in the text as laid out: up to where the
// next character starts, so it carries the kerning between them (or, at the
// end of a word or line, its own width). In order, as SplitText's chars.
const naturalAdvances = (target: Element) => {
    const out: { char: string; width: number }[] = []
    const range = document.createRange()
    const walker = document.createTreeWalker(target, NodeFilter.SHOW_TEXT)
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        const text = node.textContent ?? ''
        const rects: (DOMRect | null)[] = []
        let offset = 0
        const parts = Array.from(text)
        for (const part of parts) {
            if (part.trim()) {
                range.setStart(node, offset)
                range.setEnd(node, offset + part.length)
                rects.push(range.getBoundingClientRect())
            } else rects.push(null)
            offset += part.length
        }
        rects.forEach((rect, i) => {
            if (!rect) return
            const next = rects[i + 1]
            out.push({
                char: parts[i],
                width: next && next.top === rect.top ? next.left - rect.left : rect.width,
            })
        })
    }
    return out
}

// Splits the text into per-character boxes that each hold their real glyph's
// width, ready for random glyphs to be swapped in without moving the line.
const splitForScramble = (target: Element) => {
    // Read before splitting: split into separate boxes, the chars lose their
    // kerning and come out wider than the text they're reverted to
    const advances = naturalAdvances(target)

    // Words keep each word on one line; chars are what animate
    const split = SplitText.create(target, { type: 'words,chars' })
    const chars = split.chars as HTMLElement[]
    const real = chars.map((char) => char.textContent ?? '')
    const glyphs = real.map(pool)

    // Measure every width before changing any style: interleaving reads and
    // writes would force a fresh layout of the whole line per character.
    // Should the split not line up with the text read before it, the chars'
    // own (unkerned) widths will do.
    const widths =
        advances.length === chars.length && advances.every((a, i) => a.char === real[i])
            ? advances.map((a) => a.width)
            : chars.map((char) => char.getBoundingClientRect().width)
    chars.forEach((char, i) => {
        // Hold the real glyph's width, so random letters don't push the line
        // around. No layout containment: it moves an inline-block's baseline to
        // its bottom edge, raising every char and making each line taller than
        // the text it's reverted to.
        Object.assign(char.style, {
            display: 'inline-block',
            width: `${widths[i]}px`,
            textAlign: 'center',
            contain: 'style',
            willChange: 'transform, filter, opacity',
        })
    })
    target.setAttribute('data-scrambling', '')

    return { split, chars, real, glyphs }
}

/**
 * Brings text in character by character: each glyph rises from below through
 * a blur while briefly cycling random letters, then lands on its real
 * character. Modelled on SwiftUI's contentTransition(.numericText()) with a
 * spring(response: 0.45, dampingFraction: 0.8).
 *
 * Works on any element with text, keeping its markup (spans, <br>s) intact.
 * The split is undone once it finishes, so the text is left exactly as it was
 * (selectable, and read normally by screen readers throughout).
 *
 * Give the element `visibility: hidden` in CSS to avoid a flash of the text
 * before the animation starts; this reveals it.
 */
export function scrambleBlurIn(target: Element, options: ScrambleBlurInOptions = {}): gsap.core.Timeline {
    const o = { ...DEFAULTS, ...options }
    const tl = gsap.timeline({ delay: o.delay, onComplete: o.onComplete })

    gsap.set(target, { visibility: 'visible' })

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return tl.from(target, { opacity: 0, duration: 0.4, ease: 'power1.out' })
    }

    const { split, chars, real, glyphs } = splitForScramble(target)
    const lastSwap = chars.map(() => 0)

    tl.fromTo(
        chars,
        {
            opacity: 0,
            yPercent: o.rise * 100,
            filter: `blur(${o.blur}px)`,
        },
        {
            opacity: 1,
            yPercent: 0,
            filter: 'blur(0px)',
            duration: o.duration,
            // A light overshoot, like the damped spring on iOS
            ease: 'back.out(1.15)',
            stagger: o.stagger,
            onUpdate(this: gsap.core.Tween) {
                // One tween drives every char; work out each char's own progress
                const now = performance.now()
                const time = this.time()
                for (let i = 0; i < chars.length; i++) {
                    const set = glyphs[i]
                    if (!set) continue
                    const progress = (time - i * o.stagger) / o.duration
                    if (progress <= 0) continue
                    if (progress >= o.scramble) {
                        if (lastSwap[i] !== -1) {
                            chars[i].textContent = real[i]
                            lastSwap[i] = -1
                        }
                        continue
                    }
                    if (now - lastSwap[i] < SWAP_MS) continue
                    lastSwap[i] = now
                    chars[i].textContent = set[Math.floor(Math.random() * set.length)]
                }
            },
            onComplete: () => {
                split.revert()
                target.removeAttribute('data-scrambling')
            },
        }
    )

    return tl
}

/**
 * The reverse of scrambleBlurIn: each character starts cycling random glyphs,
 * then lifts away through a blur and fades out, one after another. Same
 * options; `rise` is how far up each character drifts as it leaves.
 *
 * Leaves the element with `visibility: hidden` (the split is undone first, so
 * its markup is as it was): collapse or remove it afterwards if needed.
 */
export function scrambleBlurOut(target: Element, options: ScrambleBlurInOptions = {}): gsap.core.Timeline {
    const o = { ...DEFAULTS, duration: 0.45, stagger: 0.012, ...options }
    const hide = () => gsap.set(target, { visibility: 'hidden' })
    const tl = gsap.timeline({
        delay: o.delay,
        onComplete: () => {
            hide()
            o.onComplete?.()
        },
    })

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return tl.to(target, { opacity: 0, duration: 0.3, ease: 'power1.in' })
    }

    const { split, chars, real, glyphs } = splitForScramble(target)
    const lastSwap = chars.map(() => 0)
    // Scramble over the start of each char's exit, while it's still readable
    const scrambleUntil = o.scramble

    tl.to(chars, {
        opacity: 0,
        yPercent: -o.rise * 100,
        filter: `blur(${o.blur}px)`,
        duration: o.duration,
        ease: 'power2.in',
        stagger: o.stagger,
        onUpdate(this: gsap.core.Tween) {
            const now = performance.now()
            const time = this.time()
            for (let i = 0; i < chars.length; i++) {
                const set = glyphs[i]
                if (!set) continue
                const progress = (time - i * o.stagger) / o.duration
                if (progress <= 0 || progress >= scrambleUntil) continue
                if (now - lastSwap[i] < SWAP_MS) continue
                lastSwap[i] = now
                chars[i].textContent = set[Math.floor(Math.random() * set.length)]
            }
        },
        onComplete: () => {
            // Put the real text back before undoing the split, then keep it hidden
            chars.forEach((char, i) => (char.textContent = real[i]))
            split.revert()
            target.removeAttribute('data-scrambling')
            hide()
        },
    })

    return tl
}

/**
 * React hook for scrambleBlurIn: runs it on mount and cleans up (stopping the
 * animation and restoring the text) on unmount.
 *
 * Starts two frames after mount, once the page has painted: mounting usually
 * coincides with React rendering the page, a long task that would otherwise
 * swallow the animation's first frames.
 *
 * It reveals the element by setting `visibility: visible` on it, which shows
 * it even inside a hidden parent: pass `enabled` false when it shouldn't
 * come in at all.
 */
export function useScrambleBlurIn(
    ref: React.RefObject<Element | null>,
    options?: ScrambleBlurInOptions,
    // False to leave the element as it is (read on mount only)
    enabled = true
) {
    useGSAP(
        (_context, contextSafe) => {
            if (!enabled) return
            const start = contextSafe?.(() => {
                if (ref.current) scrambleBlurIn(ref.current, options)
            })
            if (!start) return
            let frame = requestAnimationFrame(() => {
                frame = requestAnimationFrame(start)
            })
            return () => cancelAnimationFrame(frame)
        },
        { dependencies: [], scope: ref }
    )
}
